/**
 * AccountMergeCard — High King-only UI for linking/unlinking two
 * user accounts so they share display totals (XP, coins, lifetime
 * stats). Renders inside AdminPanel.
 *
 * Created Feb 12 2026 for the @theantagonistari spec:
 *   admin@smarteryou.live + theantagonistari@gmail.com share XP/coins
 *   while retaining their separate Admin/High King vs King roles.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link2, Unlink, Users, Loader2, X, Crown } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AccountMergeCard = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [primary, setPrimary] = useState('admin@smarteryou.live');
  const [partner, setPartner] = useState('theantagonistari@gmail.com');
  const [linking, setLinking] = useState(false);
  const [unlinking, setUnlinking] = useState('');
  // Feb 12 2026 user spec — one-tap "Crown @theantagonistari King of UK"
  // chain: link + appoint in a single click. Disabled until both fields
  // are populated.
  const [crowning, setCrowning] = useState(false);

  const linkUkKingChain = async () => {
    if (!partner.trim()) {
      toast.error('Partner email is required to crown the King of UK');
      return;
    }
    setCrowning(true);
    try {
      // Step 1 — Link the two accounts (idempotent — re-running on an
      // already-linked pair is a no-op that returns the existing group).
      const linkRes = await authFetch(`${BACKEND_URL}/api/admin/users/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primary_email: primary.trim(),
          partner_email: partner.trim(),
        }),
      });
      const linkData = await linkRes.json().catch(() => ({}));
      if (!linkRes.ok) {
        toast.error(`Link failed: ${linkData.detail || 'unknown error'}`);
        return;
      }
      // Step 2 — Crown the partner King of UK in slot 002.
      const crownRes = await authFetch(`${BACKEND_URL}/api/admin/king/appoint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: partner.trim(),
          slot_number: '002',
        }),
      });
      const crownData = await crownRes.json().catch(() => ({}));
      if (!crownRes.ok) {
        toast.error(`Crown failed: ${crownData.detail || 'unknown error'} (the link itself worked — fix the crown step manually).`);
        await refresh();
        return;
      }
      toast.success(`Linked + crowned ${partner.trim()} as ${crownData.crest_id} of ${crownData.country}.`);
      await refresh();
    } catch {
      toast.error('Network error during crown chain');
    } finally {
      setCrowning(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/users/merge-groups`);
      if (r.ok) {
        const d = await r.json();
        setGroups(d.groups || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onLink = async (e) => {
    e?.preventDefault?.();
    if (!primary.trim() || !partner.trim()) {
      toast.error('Both emails are required');
      return;
    }
    setLinking(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/users/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          primary_email: primary.trim(),
          partner_email: partner.trim(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not link accounts');
        return;
      }
      toast.success(`Linked ${d.primary_email} ↔ ${d.partner_email}`);
      setPartner('');
      await refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setLinking(false);
    }
  };

  const onUnlink = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from their merge group?`)) return;
    setUnlinking(userId);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/admin/users/unlink/${userId}`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || 'Could not unlink');
        return;
      }
      toast.success(`Unlinked ${email}`);
      await refresh();
    } catch {
      toast.error('Network error');
    } finally {
      setUnlinking('');
    }
  };

  return (
    <section
      className="rounded-2xl border border-fuchsia-500/30 bg-slate-950/40 p-5 space-y-4"
      data-testid="account-merge-card"
    >
      <div className="flex items-center gap-2">
        <Link2 className="w-5 h-5 text-fuchsia-300" />
        <h2 className="text-lg font-bold text-white">Link Accounts (shared XP &amp; coins)</h2>
      </div>
      <p className="text-xs text-slate-400">
        Linked accounts <strong className="text-fuchsia-200">share their coin + XP totals</strong> for display. Each account keeps its own role, sessions and ledger — only the displayed sum is fused.
      </p>

      <form onSubmit={onLink} className="grid sm:grid-cols-2 gap-2">
        <input
          type="email"
          value={primary}
          onChange={(e) => setPrimary(e.target.value)}
          placeholder="primary email"
          className="rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40"
          data-testid="account-merge-primary-email"
          disabled={linking}
        />
        <input
          type="email"
          value={partner}
          onChange={(e) => setPartner(e.target.value)}
          placeholder="partner email"
          className="rounded-lg bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40"
          data-testid="account-merge-partner-email"
          disabled={linking}
        />
        <button
          type="submit"
          disabled={linking}
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-bold px-4 py-2 disabled:opacity-50"
          data-testid="account-merge-link-btn"
        >
          {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Link Accounts
        </button>
        <button
          type="button"
          onClick={linkUkKingChain}
          disabled={crowning}
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-lg border border-amber-400/60 bg-amber-400/15 hover:bg-amber-400/25 text-amber-100 text-sm font-bold px-4 py-2 disabled:opacity-50"
          data-testid="account-merge-crown-uk-btn"
          title="Link the two emails AND crown the partner as King of UK in slot 002"
        >
          {crowning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
          Link + Crown King of UK
        </button>
      </form>

      <div className="border-t border-slate-800 pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-fuchsia-300" />
            <h3 className="text-sm font-bold text-white">Active links</h3>
          </div>
          <span className="text-[11px] text-slate-500 tabular-nums">{groups.length} groups</span>
        </div>
        {loading ? (
          <p className="text-xs text-slate-400 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
          </p>
        ) : groups.length === 0 ? (
          <p className="text-xs text-slate-400" data-testid="account-merge-empty">
            No linked accounts yet. Link a pair above to start fusing their totals.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="account-merge-groups">
            {groups.map((g) => (
              <li key={g.group_id} className="rounded-xl border border-slate-700 bg-slate-900/40 p-3 space-y-1.5">
                <p className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
                  {g.group_id}
                </p>
                {g.members.map((m) => (
                  <div key={m.user_id} className="flex items-center justify-between gap-2 text-sm" data-testid={`account-merge-member-${m.user_id}`}>
                    <span className="truncate">
                      <span className="font-bold text-white">{m.name}</span>
                      <span className="text-slate-400 text-xs"> · {m.email}</span>
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-200">{m.role}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => onUnlink(m.user_id, m.email)}
                      disabled={unlinking === m.user_id}
                      className="inline-flex items-center gap-1 text-[10px] rounded-md bg-rose-500/20 hover:bg-rose-500/40 text-rose-200 px-2 py-1 disabled:opacity-50"
                      data-testid={`account-merge-unlink-${m.user_id}`}
                    >
                      {unlinking === m.user_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />}
                      Unlink
                    </button>
                  </div>
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default AccountMergeCard;
